import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell, AlertTriangle, ClipboardCheck, FileText, GraduationCap, CheckCircle2 } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getNotifications, markNotificationRead, markAllNotificationsRead, type Notification } from '../../lib/services/notifications'
import { useAuth } from '../../context/AuthContext'
import { useRealtimeNotifications } from '../../hooks/useRealtimeNotifications'
import { protectedQueryKey } from '../../lib/queryClient'

function notificationIcon(type: string) {
  if (type.includes('alert') || type.includes('overdue')) return <AlertTriangle size={14} className="text-rose-500" />
  if (type.includes('action') || type.includes('capa')) return <ClipboardCheck size={14} className="text-amber-500" />
  if (type.includes('report')) return <FileText size={14} className="text-blue-500" />
  if (type.includes('training')) return <GraduationCap size={14} className="text-purple-500" />
  if (type.includes('verified') || type.includes('completed')) return <CheckCircle2 size={14} className="text-emerald-500" />
  return <Bell size={14} className="text-slate-400" />
}

function entityLink(n: Notification): string {
  if (n.entity_type === 'corrective_action' && n.entity_id) return `/actions/${n.entity_id}`
  if (n.entity_type === 'report' && n.entity_id) return `/reports/${n.entity_id}`
  if (n.entity_type === 'visit' && n.entity_id) return `/visits/${n.entity_id}`
  return '#'
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function NotificationBell() {
  const { viewerContext } = useAuth()
  const queryClient = useQueryClient()
  const [panelOpen, setPanelOpen] = useState(false)
  const [toast, setToast] = useState<Notification | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const identity = viewerContext
    ? { userId: viewerContext.user_id, tenantId: viewerContext.tenant_id }
    : null
  const notificationsQueryKey = protectedQueryKey(identity, 'notifications')
  const { data } = useQuery({
    queryKey: notificationsQueryKey,
    queryFn: () => getNotifications(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    enabled: Boolean(identity),
  })

  const notifications = data?.notifications ?? []
  const unreadCount = data?.unread_count ?? 0

  // Close panel on outside click
  useEffect(() => {
    if (!panelOpen) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setPanelOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [panelOpen])

  // Realtime: show toast + invalidate identity-scoped notification queries
  const handleNewNotification = useCallback((n: Notification) => {
    setToast(n)
    queryClient.invalidateQueries({ queryKey: notificationsQueryKey })
    setTimeout(() => setToast(null), 5000)
  }, [queryClient, notificationsQueryKey])

  useRealtimeNotifications(viewerContext?.user_id, handleNewNotification)

  const handleMarkRead = async (id: string) => {
    await markNotificationRead(id)
    queryClient.invalidateQueries({ queryKey: notificationsQueryKey })
  }

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead()
    queryClient.invalidateQueries({ queryKey: notificationsQueryKey })
  }

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="fixed right-4 top-20 z-50 animate-[slideIn_0.3s_ease-out] rounded-xl border border-slate-200 bg-white p-3 shadow-lg" style={{ maxWidth: 340 }}>
          <div className="flex items-start gap-2">
            {notificationIcon(toast.notification_type)}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-slate-800">{toast.title}</p>
              <p className="mt-0.5 text-xs text-slate-500">{toast.body}</p>
            </div>
            <button onClick={() => setToast(null)} className="text-slate-400 hover:text-slate-600">&times;</button>
          </div>
        </div>
      )}

      {/* Bell + Panel */}
      <div className="relative" ref={panelRef}>
        <button
          onClick={() => setPanelOpen(!panelOpen)}
          className={`relative rounded-lg border p-2 transition-all ${panelOpen ? 'border-[var(--brand-primary)] bg-[var(--brand-light)]' : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}
          title="Notifications"
        >
          <Bell size={16} />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-extrabold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {panelOpen && (
          <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <span className="font-brand text-sm font-bold text-[var(--brand-deep)]">Notifications</span>
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className="text-xs font-bold text-[var(--brand-secondary)] hover:underline">
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 && (
                <p className="px-4 py-8 text-center text-sm text-slate-400">No notifications</p>
              )}
              {notifications.slice(0, 20).map(n => (
                <Link
                  key={n.notification_id}
                  to={entityLink(n)}
                  onClick={() => {
                    if (!n.read_at) handleMarkRead(n.notification_id)
                    setPanelOpen(false)
                  }}
                  className={`flex gap-3 border-b border-slate-50 px-4 py-3 transition-colors hover:bg-slate-50 ${!n.read_at ? 'bg-blue-50/40' : ''}`}
                >
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                    {notificationIcon(n.notification_type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-800">{n.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{n.body}</p>
                    <p className="mt-1 text-[10px] font-semibold text-slate-400">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read_at && <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-primary)]" />}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
